import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useJsonLd, generateOrganizationSchema, generateFAQSchema, generateBreadcrumbSchema } from '@/utils/seo';

const faqs = [
  {
    question: '丫丫漫画是免费的吗？',
    answer: '是的，丫丫漫画平台上的所有漫画全部免费阅读，无需任何付费订阅。我们致力于为广大漫画爱好者提供高品质、零门槛的阅读体验。',
  },
  {
    question: '丫丫漫画支持哪些类型的漫画？',
    answer: '丫丫漫画涵盖国漫、日漫、韩漫、美漫等多种类型，包括热血、玄幻、恋爱、悬疑、校园、武侠、科幻等丰富题材，满足不同读者的口味需求。',
  },
  {
    question: '如何在丫丫漫画上阅读漫画？',
    answer: '丫丫漫画采用创新的下拉式翻页阅读模式，您只需在手机或电脑上打开网站，选择喜欢的漫画，向下滑动即可流畅阅读，无需点击翻页按钮，体验如同刷短视频一样轻松自然。',
  },
  {
    question: '丫丫漫画的更新频率是怎样的？',
    answer: '我们每日更新热门漫画最新章节，确保读者能够及时追更。热门连载漫画通常每周更新1-3话，已完结漫画可一次性畅读全集。',
  },
  {
    question: '如何联系丫丫漫画客服？',
    answer: '您可以通过邮箱 support@jxjdxwx.cn 联系我们，工作时间为周一至周五 9:00-18:00。我们将在24小时内回复您的咨询。',
  },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 丫丫漫画';
  }, []);

  const breadcrumbItems = [
    { name: '首页', url: '/' },
    { name: '关于我们' },
  ];

  useJsonLd(generateOrganizationSchema());
  useJsonLd(
    generateFAQSchema(faqs)
  );
  useJsonLd(
    generateBreadcrumbSchema([
      { name: '首页', url: 'https://jxjdxwx.cn/' },
      { name: '关于我们', url: 'https://jxjdxwx.cn/about' },
    ])
  );

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-24 pb-6 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <section className="px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-900">关于丫丫漫画</h1>
              <p className="mt-3 text-sm text-foreground-500 max-w-2xl mx-auto">
                致力于为漫画爱好者提供最优质的在线阅读体验
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-16">
              <div>
                <h2 className="text-xl font-heading text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  丫丫漫画创立于2024年，由一群热爱漫画的年轻人共同打造。我们深信，好的漫画应该被更多人看到，优秀的阅读体验不应该被付费门槛所阻挡。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  从最初的一个小网站，到如今汇聚了国漫、日漫、韩漫等多种类型的综合性漫画平台，丫丫漫画始终坚持以用户为中心，不断优化阅读体验，让每一位漫画爱好者都能在这里找到属于自己的快乐。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的团队分布在全国各地，用心筛选每一部优质漫画，确保内容质量与更新速度。丫丫漫画，让漫画阅读变得更简单、更快乐。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20creative%20workspace%20with%20comic%20books%20and%20drawing%20tablet%2C%20artistic%20atmosphere%2C%20soft%20natural%20light%20streaming%20through%20window%2C%20plants%20and%20bookshelves%2C%20modern%20minimalist%20interior%2C%20warm%20coral%20and%20cream%20color%20palette%2C%20clean%20aesthetic%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="丫丫漫画工作室"
                  title="丫丫漫画 - 创作环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-heading text-foreground-900">我们的优势</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-8 rounded-xl bg-background-50 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-5">
                  <i className="ri-flashlight-line text-3xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-heading text-foreground-900 mb-3">闪电加载</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">采用先进的图片加载技术，漫画页面秒开，无需漫长等待，让阅读体验行云流水。</p>
              </div>
              <div className="p-8 rounded-xl bg-background-50 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-5">
                  <i className="ri-device-line text-3xl text-accent-500"></i>
                </div>
                <h3 className="text-lg font-heading text-foreground-900 mb-3">全平台适配</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">完美适配手机、平板、电脑等各类设备，随时随地享受漫画阅读的乐趣。</p>
              </div>
              <div className="p-8 rounded-xl bg-background-50 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-200 mx-auto mb-5">
                  <i className="ri-shield-check-line text-3xl text-secondary-600"></i>
                </div>
                <h3 className="text-lg font-heading text-foreground-900 mb-3">海量正版</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">与多家漫画平台合作，提供丰富正版漫画资源，画质清晰，更新及时，让读者看得放心。</p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl font-heading text-foreground-900 mb-6">读者口碑</h2>
                <div className="space-y-5">
                  <div className="p-5 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                      "丫丫漫画的下拉式翻页真的太方便了！再也不用点来点去了，刷漫画跟刷短视频一样停不下来。推荐给了好多朋友，都说好用！"
                    </p>
                    <p className="text-xs text-foreground-400">—— 漫画迷小陈</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                      "国漫、日漫、韩漫种类很全，更新也很快，每天都有新漫画看。关键是免费！画质还特别好，简直是漫画爱好者的天堂。"
                    </p>
                    <p className="text-xs text-foreground-400">—— 日漫爱好者小美</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                      "一直在找能流畅看韩漫的平台，试了好几个都不满意，直到发现了丫丫漫画。界面清爽，加载快，漫画资源又多，爱了爱了！"
                    </p>
                    <p className="text-xs text-foreground-400">—— 韩漫达人阿强</p>
                  </div>
                </div>
              </div>
              <div>
                <h2 className="text-xl font-heading text-foreground-900 mb-6">联系我们</h2>
                <div className="p-6 rounded-xl bg-background-100">
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 shrink-0">
                        <i className="ri-mail-line text-xl text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                        <p className="text-sm text-foreground-500 mt-0.5">support@jxjdxwx.cn</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 shrink-0">
                        <i className="ri-time-line text-xl text-accent-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900">工作时间</h4>
                        <p className="text-sm text-foreground-500 mt-0.5">周一至周五 9:00 - 18:00</p>
                        <p className="text-xs text-foreground-400 mt-1">节假日休息，紧急事项请发邮件</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-200 shrink-0">
                        <i className="ri-map-pin-line text-xl text-secondary-600"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900">公司地址</h4>
                        <p className="text-sm text-foreground-500 mt-0.5">中国北京市朝阳区望京SOHO T3-A座 1208室</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 shrink-0">
                        <i className="ri-phone-line text-xl text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900">客服电话</h4>
                        <p className="text-sm text-foreground-500 mt-0.5">400-888-8888</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading text-foreground-900 text-center mb-10">常见问题</h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-50 overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors">
                    {faq.question}
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform duration-200 text-foreground-400"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}